import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

import { type RootState } from "@/rootReducer";

import {
  fetchData,
  fetchPdfInfo,
  getRateScheduleOptions,
  parseApiError,
  ParsedApiError,
  postApplyForFinancing,
  postDesignOptimization,
  postPricingCalculation,
  syncData,
} from "../../lib/api/proposal-api";
import { getReportHash } from "../../lib/selectors";
import { CalculatePricingAction, EnerfloProposalData, OptimizeDesignAction, ProposalPdfInfo, SelectOption } from "../../types";

type ThunkApi<RejectValue = never> = { state: RootState; rejectValue: RejectValue };

export const fetchProposalData = createAsyncThunk<EnerfloProposalData, string, ThunkApi<ParsedApiError>>(
  "proposal/fetch",
  async (reportHash, thunkAPI) => {
    try {
      const response = await fetchData(reportHash, {
        params: {
          type: thunkAPI.getState().proposal.demoType,
          cacheMode: thunkAPI.getState().proposal.cacheMode,
        },
      });
      return response.data;
    } catch (e: any) {
      return thunkAPI.rejectWithValue(parseApiError(e));
    }
  }
);

export const fetchProposalPdfInfo = createAsyncThunk<ProposalPdfInfo, string, ThunkApi<ParsedApiError>>(
  "proposal/fetchPdfInfo",
  async (reportHash, thunkAPI) => {
    try {
      const response = await fetchPdfInfo(reportHash);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(parseApiError(e as AxiosError));
    }
  }
);

export const refreshDesignData = createAsyncThunk<EnerfloProposalData, undefined, ThunkApi>(
  "proposal/refresh",
  async (_arg, thunkAPI) => {
    const reportHash = getReportHash(thunkAPI.getState());
    const response = await fetchData(reportHash!, { params: { refresh: 1 } });
    return response.data;
  }
);

export const syncDesignData = createAsyncThunk<EnerfloProposalData, undefined, ThunkApi>(
  "proposal/sync",
  async (_arg, thunkAPI) => {
    const reportHash = getReportHash(thunkAPI.getState());
    const response = await syncData(reportHash);
    return response.data;
  }
);

type RejectWithErrors = { errors: Record<any, string[]>; details?: any };

export const submitOptimization = createAsyncThunk<
  EnerfloProposalData,
  OptimizeDesignAction,
  ThunkApi<RejectWithErrors>
>("data/submitOptimization", async (payload: OptimizeDesignAction, thunkAPI) => {
  const reportHash = getReportHash(thunkAPI.getState());
  const { data = {}, type: _type } = payload;

  try {
    const response = await postDesignOptimization(reportHash, { ...data, _type });

    return response.data;
  } catch (e) {
    let error = e as AxiosError<any>;

    // Check for this error format
    if (error.response?.data["message"]) {
      return thunkAPI.rejectWithValue({ errors: error.response?.data["message"] });
    }
    return thunkAPI.rejectWithValue({ errors: error.response?.data?.errors ?? {} });
  }
});

export const applyForFinancing = createAsyncThunk<{ redirectUrl: string }, void, ThunkApi<ParsedApiError>>(
  "proposal/applyForFinancing",
  async (_arg, thunkAPI) => {
    const reportHash = getReportHash(thunkAPI.getState());

    try {
      const response = await postApplyForFinancing(reportHash);

      return response.data;
    } catch (e) {
      let error = e as AxiosError<any>;

      // Check for this error format
      return thunkAPI.rejectWithValue(parseApiError(error));
    }
  }
);

export const recalculatePricing = createAsyncThunk<
  EnerfloProposalData,
  CalculatePricingAction,
  ThunkApi<RejectWithErrors>
>("proposal/recalculatePricing", async (action, thunkAPI) => {
  const reportHash = getReportHash(thunkAPI.getState());
  const { type: _type, data = {} } = action;

  try {
    const response = await postPricingCalculation(reportHash, _type, { ...data, _type });
    return response.data;
  } catch (e) {
    let error = e as AxiosError<any>;

    // Check for this error format
    if (error.response?.data["message"]) {
      return thunkAPI.rejectWithValue({
        errors: error.response?.data["message"],
        details: error.response?.data["details"],
      });
    }
    return thunkAPI.rejectWithValue({ errors: error.response?.data?.errors ?? {} });
  }
});

export const fetchRateScheduleOptions = createAsyncThunk<SelectOption[], undefined, ThunkApi<ParsedApiError>>(
  "proposal/fetchRateScheduleOptions",
  async (_arg, thunkAPI) => {
    const reportHash = getReportHash(thunkAPI.getState());

    try {
      const response = await getRateScheduleOptions(reportHash);
      return response.data.options;
    } catch (e: any) {
      return thunkAPI.rejectWithValue(parseApiError(e));
    }
  }
);
